import { useEffect, useRef, useState } from 'react';
import { useActiveConnector } from '../../contexts/ConnectorContext';

interface Option {
    id: number | string;
    name: string;
}

interface MultiSelectFilterProps {
    label: string;
    endpoint: string;
    selected: (number | string)[];
    onChange: (ids: (number | string)[]) => void;
}

export function MultiSelectFilter({ label, endpoint, selected, onChange }: MultiSelectFilterProps) {
    const activeConnector = useActiveConnector();
    const connectorId = activeConnector.id;
    const [options, setOptions] = useState<Option[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setLoading(true);
        setOptions([]);
        onChange([]);
        const token = localStorage.getItem('auth_token');
        const headers: HeadersInit = {
            Accept: 'application/json',
            'X-Connector-Id': String(connectorId),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        fetch(endpoint, { headers })
            .then((res) => res.json())
            .then((json: { data?: Option[] }) => {
                setOptions(json.data ?? []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint, connectorId]);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const filtered = search
        ? options.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()))
        : options;

    function toggle(id: number | string) {
        const next = selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id];
        onChange(next);
    }

    const selectedNames = options
        .filter((o) => selected.includes(o.id))
        .map((o) => o.name);

    const buttonText = loading
        ? 'Chargement...'
        : selectedNames.length === 0
          ? 'Tous'
          : selectedNames.length <= 2
            ? selectedNames.join(', ')
            : `${selectedNames.length} sélectionnés`;

    return (
        <div ref={ref} className="relative">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap block mb-1">
                {label}
            </label>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white min-w-[160px] text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
                <span className="truncate">{buttonText}</span>
                <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <div className="p-2 border-b border-gray-100">
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                    </div>
                    <div className="max-h-48 overflow-y-auto p-1">
                        {selected.length > 0 && (
                            <button
                                type="button"
                                onClick={() => onChange([])}
                                className="w-full text-left px-2 py-1 text-xs text-brand-600 hover:bg-brand-50 rounded"
                            >
                                Tout effacer
                            </button>
                        )}
                        {filtered.map((option) => (
                            <label
                                key={option.id}
                                className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-gray-50 rounded cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.includes(option.id)}
                                    onChange={() => toggle(option.id)}
                                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                                />
                                <span className="truncate">{option.name}</span>
                            </label>
                        ))}
                        {filtered.length === 0 && (
                            <div className="px-2 py-3 text-sm text-gray-400 text-center">
                                Aucun résultat
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
